import React, { useEffect, useState } from 'react';
import * as Yup from "yup";
import Breadcrumbs from '../../components/BreadCrumbs';
import { toast } from 'react-toastify';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Form, Formik } from 'formik';
import Button from '../../components/buttons/Button';
import InputField from '../../components/inputFields/InputField';
import ImagePicker from '../../components/inputFields/ImagePicker';
import { getSubCategories, patchCategory, postCategory } from '../../services/domains';
import SearchBar from '../../components/searchBar/SearchBar';
import CustomLoading from '../../components/customLoading/CustomLoading';
import { BiEdit, BiRefresh } from 'react-icons/bi';
import Table from '../../components/tables/Table';
import Pagination from '../../components/tables/pagination/Pagination';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';

function CategoryForm() {
    const location = useLocation();
    const navigate = useNavigate();
    const [rowData, setRowData] = useState(null);
    const [categoryData, setCategoryData] = useState(null);
    const [image, setImage] = useState(null);

    const handleImageSelect = (file) => {
        setImage(file);
    };

    useEffect(() => {
        if (location.state) {
            if (location.state.item) {
                setCategoryData(location.state.item);
                if (location.state.item.image) {
                    setImage(location.state.item.image);
                    fetchData({ query: "", categoryId: location.state.item.id });
                }
            }
            if (location.state.domain) {
                setRowData(location.state.domain);
            }
        }
    }, [location]);

    const handleDiscardChanges = (resetForm) => {
        resetForm();
        navigate(-1);
    };

    const handleFormSubmit = async (values, { setSubmitting }) => {
        const data = new FormData();
        if (image && typeof image === "object") {
            data.append("image", image);
        }
        if (categoryData) {
            data.append("domain", rowData.id);
        }
        data.append("name", values.name);
        data.append("description", values.description);

        try {
            const response = categoryData ? await patchCategory({ data: data, id: categoryData.id, }) : await postCategory(data);

            if (response?.status === 201 || response?.status === 200) {
                toast.success(categoryData ? "Category Updated Successfully" : "Category Added successfully!");
                navigate(-1);
            } else {
                let errors = "Opps! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Opps! Something went wrong, please try again.";
            toast.error(errors);
        }
        setSubmitting(false);
    };

    // For categories table
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const headers = [
        { label: "Name", key: "name" },
        { label: "Order", key: "order" },
        { label: "Image", key: "image" },
        { label: "Description", key: "description" },
        { label: "Product Count", key: "shopproduct_count" },
        { label: "Action", key: "action" },
    ];

    const [searchText, setSearchText] = useState("");

    const pageSize = 10;

    const handleSearch = (query) => {
        setSearchText(query);
        fetchData(`?search=${query}`);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const fetchData = async ({ query, categoryId }) => {
        setLoading(true);
        try {
            let res = await getSubCategories({ query: query, categoryId: categoryId });
            if (res.status === 200) {
                const data = res.data;
                setData(data);
            } else {
                toast.error("Unable to fetch Data");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const resetFilters = () => {
        setSearchText("");
        fetchData("");
    };

    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/dashboard' },
            { name: 'Bazzar', },
            { name: 'Domains', },
            { name: 'Categories', },
            { name: categoryData ? 'Edit Category' : 'Add Category' },
        ]} />
        <div className="mt-8 gap-4">
            <Formik
                initialValues={categoryData || {
                    name: "",
                    description: "",
                    order: "",
                }}
                enableReinitialize
                validationSchema={Yup.object({
                    name: Yup.string().required("Please enter a name"),
                })}
                onSubmit={handleFormSubmit}
            >
                {({ isSubmitting, resetForm }) => (
                    <Form>
                        <div className="border border-gray-300 mt-5 p-4 rounded-lg bg-gray-100 gap-2">
                            <div className="flex flex-row gap-4">
                                <div className="h-28 w-28 aspect-square">
                                    <ImagePicker
                                        preloadedImage={image}
                                        onImageSelect={handleImageSelect}
                                        placeholder='Select Logo'
                                    />
                                </div>
                                <div className="grid gap-2 grid-cols-1 md:grid-cols-3 w-full">
                                    <InputField
                                        label="Name"
                                        name="name"
                                        placeholder="Name"
                                    />
                                    <InputField
                                        label="Description"
                                        name="description"
                                        type="text"
                                        placeholder="Write your description here..."
                                    />
                                    <InputField
                                        label="Order"
                                        name="order"
                                        type="number"
                                        placeholder="X"
                                    />
                                </div>
                            </div>
                            {/* Buttons */}
                            <div className="flex justify-end gap-6">
                                <div>
                                    <Button
                                        type="button"
                                        className="text-primary bg-white hover:bg-white-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center border border-primary"
                                        onClick={() => handleDiscardChanges(resetForm)}
                                        disabled={isSubmitting}
                                    >
                                        Discard Changes
                                    </Button>
                                </div>
                                <div>
                                    <Button
                                        type="submit"
                                        className="text-white bg-primary hover:bg-primary-dark font-sm rounded-lg text-lg w-full px-8 py-1.5 text-center"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? "Saving..." : "Save"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
        {
            rowData ?
                <div className="rounded-lg border bg-white mt-4 p-4">
                    <div className="flex my-1">
                        <StyledHeadingText text={"Sub Categories"} />
                    </div>
                    <div className="flex flex-row justify-between item-center">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 justify-center items-center max-w-4xl py-2">
                            <SearchBar placeholder="Search by name" onSearch={handleSearch} value={searchText} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4 justify-center items-center">
                                <div className="flex flex-row items-center gap2">
                                    {
                                        searchText.length !== 0 ?
                                            <BiRefresh size={24} className='cursor-pointer text-primary' onClick={resetFilters} /> : <></>
                                    }
                                </div>
                            </div>
                        </div>
                        <Link to={`/bazzar/domains/categories/sub-categories/add`} state={{ domain: rowData }} className='self-center'>
                            <Button
                                type="button"
                                className="hover:bg-black hover:opacity-70 text-white bg-black rounded-md"
                            >
                                Add Sub Category
                            </Button>
                        </Link>
                    </div>
                    {
                        loading ?
                            <div className="flex w-full flex-row justify-center my-2">
                                <CustomLoading size={100} />
                            </div>
                            :
                            <div className="mt-2">
                                <Table
                                    headers={headers}
                                    rows={data?.results?.map((item, index) => (
                                        {
                                            name: <p>{item.name}</p>,
                                            order: <p>{item.order}</p>,
                                            description: <span className='flex flex-row gap-1 items-center'><p className="max-w-40 truncate cursor-help" title={item.description}>{item.description}</p></span>,
                                            shopproduct_count: <p>{item.shopproduct_count}</p>,
                                            image: <img src={item.image} alt={item.image} className='w-12 aspect-square rounded-sm border object-contain' />,
                                            action: <Link to={`/bazzar/domains/categories/sub-categories/${item.name}/`} state={{ item: item, category: categoryData }}><BiEdit size={24} color='green' /></Link>,
                                        }))}
                                />
                            </div>}
                    {/* Table End */}
                    {/* Pagination Begin */}
                    {
                        data.count !== 0 ?
                            <Pagination
                                currentPage={currentPage}
                                
                                pageSize={pageSize}
                                totalEntries={data?.count}
                                onPageChange={handlePageChange}
                                data={data}
                                setData={setData}
                            /> : <></>
                    }
                </div> : <></>}
    </>);
}

export default CategoryForm;